import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import {
  PrismButton,
  PrismButtonGroup,
  PrismCard,
  PrismSelectInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import {
  type DataPreferences,
  FORECAST_COLUMNS,
  type ForecastColumn,
  isSdlyColumn,
  SDLY_COLUMNS,
  type SdlyColumn,
} from 'lumen/utils/constants/market-snapshots-constants';
import type { Variance } from 'lumen/utils/constants/variance';
import { isVariance, VARIANCES } from 'lumen/utils/constants/variance';
import { localCopy } from 'tracked-toolbox';

interface ForecastOption {
  label: string;
  value: ForecastColumn;
}

export interface MarketSnapshotsDataPreferencesSignature {
  Args: {
    dataPreferences: DataPreferences;
    onSave: (preferences: DataPreferences) => void;
  };
  Element: HTMLDivElement;
}

export default class MarketSnapshotsDataPreferences extends Component<MarketSnapshotsDataPreferencesSignature> {
  @service declare intl: Services['intl'];

  @localCopy('args.dataPreferences.forecastColumn')
  declare forecastColumn: ForecastColumn;
  @localCopy('args.dataPreferences.sdlyColumn') declare sdlyColumn: SdlyColumn;
  @localCopy('args.dataPreferences.variance') declare varianceType: Variance;

  // Preserve reference for isEqual check in PowerSelect code
  @cached
  get forecastColumnOptions(): ForecastOption[] {
    return [
      {
        label: this.intl.t('market.snapshots.forecast.bi'),
        value: FORECAST_COLUMNS.BI,
      },
      {
        label: this.intl.t('market.snapshots.forecast.user'),
        value: FORECAST_COLUMNS.USER,
      },
      {
        label: this.intl.t('market.snapshots.forecast.rms'),
        value: FORECAST_COLUMNS.RMS,
      },
      {
        label: this.intl.t('market.snapshots.forecast.finance'),
        value: FORECAST_COLUMNS.FINANCE,
      },
    ];
  }

  get selectedForecastColumnOption(): ForecastOption | undefined {
    return this.forecastColumnOptions.find(
      (option) => option.value === this.forecastColumn,
    );
  }

  @action
  onCancel(dropdown?: { toggleDropdown: () => void }): void {
    this.forecastColumn = this.args.dataPreferences.forecastColumn;
    this.sdlyColumn = this.args.dataPreferences.sdlyColumn;
    this.varianceType = this.args.dataPreferences.variance;
    dropdown?.toggleDropdown();
  }

  @action
  onForecastColumnChange(option: ForecastOption): void {
    this.forecastColumn = option.value;
  }

  @action
  onSave(dropdown?: { toggleDropdown: () => void }): void {
    this.args.onSave({
      forecastColumn: this.forecastColumn,
      sdlyColumn: this.sdlyColumn,
      variance: this.varianceType,
    });
    dropdown?.toggleDropdown();
  }

  @action
  onSdlyColumnChange(sdlyColumn: string | string[]): void {
    if (isSdlyColumn(sdlyColumn)) {
      this.sdlyColumn = sdlyColumn;
    }
  }

  @action
  onVarianceTypeChange(variance: string | string[]): void {
    if (isVariance(variance)) {
      this.varianceType = variance;
    }
  }

  <template>
    <div class="relative" ...attributes>
      {{#let
        (component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          dataTestId="data-preferences-trigger"
          iconBefore="sliders"
        )
        as |triggerComponent|
      }}
        <GeneralDropdownsDropdownYield
          @dropdownClass=".dropdown-target"
          {{! @glint-expect-error: Incorrect type }}
          @dropdownClassName="dropdown-target w-[320px] top-[40px] right-0"
          @label={{t "market.snapshots.data_preferences"}}
          @onCancel={{this.onCancel}}
          @selected={{null}}
          @triggerComponent={{triggerComponent}}
          as |dropdown|
        >
          <PrismCard>
            <:header as |Header|>
              <Header.title>
                {{t "market.snapshots.data_preferences"}}
              </Header.title>
            </:header>
            <:content>
              <div class="grid gap-400">
                <div class="flex items-center justify-between">
                  <span class="text-200--medium">
                    {{t "general.variance"}}
                  </span>

                  <PrismButtonGroup
                    @onChange={{this.onVarianceTypeChange}}
                    @value={{this.varianceType}}
                    as |ButtonGroup|
                  >
                    <ButtonGroup.button
                      @label="%"
                      @value={{VARIANCES.PERCENTAGE}}
                    />
                    <ButtonGroup.button
                      @label="#"
                      @value={{VARIANCES.ABSOLUTE}}
                    />
                    <ButtonGroup.button @label="-" @value={{VARIANCES.NONE}} />
                  </PrismButtonGroup>
                </div>

                <div class="flex items-center justify-between">
                  <span class="text-200--medium">
                    {{t "market.snapshots.sdly_column"}}
                  </span>

                  <PrismButtonGroup
                    @onChange={{this.onSdlyColumnChange}}
                    @value={{this.sdlyColumn}}
                    as |ButtonGroup|
                  >
                    <ButtonGroup.button
                      @label={{t "market.snapshots.metric_type.sdly"}}
                      @value={{SDLY_COLUMNS.SDLY}}
                    />
                    <ButtonGroup.button
                      @label={{t "market.snapshots.metric_type.net_sdly"}}
                      @value={{SDLY_COLUMNS.NET_SDLY}}
                    />
                  </PrismButtonGroup>
                </div>

                <div class="flex items-center justify-between">
                  <span class="text-200--medium">
                    {{t "general.forecast"}}
                  </span>

                  <PrismSelectInput
                    @onChange={{this.onForecastColumnChange}}
                    @options={{this.forecastColumnOptions}}
                    @value={{this.selectedForecastColumnOption}}
                    data-test-id="forecast-select"
                  />
                </div>
              </div>
            </:content>
            <:footer>
              <div class="flex flex-row justify-end gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.cancel"}}
                  {{on "click" (fn this.onCancel dropdown)}}
                />
                <PrismButton
                  @label={{t "general.save"}}
                  {{on "click" (fn this.onSave dropdown)}}
                />
              </div>
            </:footer>
          </PrismCard>
        </GeneralDropdownsDropdownYield>
      {{/let}}
    </div>
  </template>
}
